/* eslint-disable max-lines-per-function */
import { useUmsatzAbsatzZiele } from '@/domain/controlling/hooks';
import { useReducer } from 'react';
import { useRouteParams } from '../useRouteParams';
import {
  formatNumber,
  getAbsatzProduktivitaetByMptOrStueck,
} from '../../../utils';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { ZieleEditForm, ZieleFormReducerAction } from '../types';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import dayjs from 'dayjs';

export function useZieleFormReducer() {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { searchParams } = useRouteParams();
  const jahr = searchParams.jahr ?? dayjs().get('year');
  const monat = searchParams.monat ?? dayjs().get('month') + 1;

  const { umsatzZiel, absatzZiel } = useUmsatzAbsatzZiele(
    standortId,
    jahr,
    monat,
  );
  const { tagebuchEinstellungen } = useTagebuchEinstellungen(standortId);

  const initalState: ZieleEditForm = {
    formValues: {
      absatzZiel: absatzZiel?.wert
        ? formatNumber(absatzZiel.wert.toString(), true)
        : '',
      absatzZielproduktivitaet:
        absatzZiel && tagebuchEinstellungen
          ? getAbsatzProduktivitaetByMptOrStueck(
              absatzZiel.produktivitaet,
              tagebuchEinstellungen.absatzProduktivitaet,
              true,
            )
          : '',
      umsatzZiel: umsatzZiel?.wert
        ? formatNumber(umsatzZiel.wert.amount.toString(), false)
        : '',
      umsatzZielproduktivitaet: umsatzZiel?.produktivitaet
        ? formatNumber(umsatzZiel.produktivitaet.amount.toString(), false)
        : tagebuchEinstellungen
          ? formatNumber(
              tagebuchEinstellungen.umsatzProduktivitaet.zielProduktivitaet.toString(),
              false,
            )
          : '',
    },
    hasChanges: false,
    isSubmitting: false,
  };

  // oxlint-disable-next-line complexity
  function zieleReducer(
    state: ZieleEditForm,
    action: ZieleFormReducerAction,
  ): ZieleEditForm {
    switch (action.type) {
      case 'updateAbsatzZiel': {
        const absatzZielValue = action.values?.absatzZiel;

        return {
          formValues: {
            ...state.formValues,
            absatzZiel: absatzZielValue,
          },
          hasChanges: absatzZielValue !== initalState.formValues.absatzZiel,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateUmsatzZiel': {
        const umsatzZielValue = action.values?.umsatzZiel;

        return {
          formValues: {
            ...state.formValues,
            umsatzZiel: umsatzZielValue,
          },
          hasChanges: umsatzZielValue !== initalState.formValues.umsatzZiel,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateUmsatzZielproduktivitaet': {
        const umsatzZielproduktivitaetValue =
          action.values?.umsatzZielproduktivitaet;

        return {
          formValues: {
            ...state.formValues,
            umsatzZielproduktivitaet: umsatzZielproduktivitaetValue,
          },
          hasChanges:
            umsatzZielproduktivitaetValue !==
            initalState.formValues.umsatzZielproduktivitaet,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'updateAbsatzZielproduktivitaet': {
        const absatzZielproduktivitaetValue =
          action.values?.absatzZielproduktivitaet;

        return {
          formValues: {
            ...state.formValues,
            absatzZielproduktivitaet: absatzZielproduktivitaetValue,
          },
          hasChanges:
            absatzZielproduktivitaetValue !==
            initalState.formValues.absatzZielproduktivitaet,
          isSubmitting: state.isSubmitting,
        };
      }
      case 'submit': {
        return {
          ...state,
          isSubmitting: true,
        };
      }
      case 'updateDialogMessage': {
        return {
          ...state,
          dialogMessage: action.dialogMessage,
        };
      }
      case 'reset': {
        return initalState;
      }
      default: {
        return state;
      }
    }
  }

  return useReducer(zieleReducer, initalState);
}
